import { Badge, Col, Image, Row, Space, Table, Tag, message } from "antd"
import React, { useEffect, useState } from "react"
import { ShoppingCartOutlined } from "@ant-design/icons"
import moment from "moment"
import { collection, doc, onSnapshot, query, updateDoc, where } from "firebase/firestore"
import { db } from "../../../../firebase/config"
import { ASAPOrder, OrderAndPrefix } from "../../../../components/Orders/Helpers/OrderAndPrefix"
import PageHeader from "../../../../components/Elements/PageHeader"
import { DndContext } from "@dnd-kit/core"
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import "../../../../styles/OrderTable.css"

const SortableRow = (props) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props["data-row-key"],
  })
  const style = {
    ...props.style,
    transform: CSS.Transform.toString(
      transform && {
        ...transform,
        scaleY: 1,
      }
    ),
    transition,
    cursor: "move",
    ...(isDragging ? { position: "relative", zIndex: 9999 } : {}),
  }
  return <tr {...props} ref={setNodeRef} style={style} {...attributes} {...listeners} />
}

export default function WinderOrders() {
  const [loading, setLoading] = useState(false)
  const [winderData, setWinderData] = useState([])
  const [winderSequence, setWinderSequence] = useState([])

  // Fetch the sequence array
  useEffect(() => {
    const sequenceDocRef = doc(db, "sequence", "documentForWinderSequence")
    const unsubscribe = onSnapshot(sequenceDocRef, (doc) => {
      if (doc.exists()) {
        const sequenceData = doc.data()
        setWinderSequence(sequenceData.winderSequence)
      }
    })
    return () => unsubscribe()
  }, [])

  // Fetch documents and sort them based on the sequence
  useEffect(() => {
    if (winderSequence.length > 0) {
      const ref = collection(db, "orders")
      let q = query(ref, where("orderStatus", "==", 5))

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const results = snapshot.docs.map((doc) => ({
          ...doc.data(),
          key: doc.id,
          id: doc.id,
        }))

        const sortedResults = results.sort((a, b) => {
          return winderSequence.indexOf(a.id) - winderSequence.indexOf(b.id)
        })

        setWinderData(sortedResults)
        setLoading(false)
      })

      return () => unsubscribe()
    }
  }, [winderSequence])

  // Handle drag end
  const onDragEnd = async ({ active, over }) => {
    if (active.id !== over?.id) {
      const newData = arrayMove(
        winderData,
        winderData.findIndex((i) => i.key === active.id),
        winderData.findIndex((i) => i.key === over?.id)
      )

      const newSequence = newData.map((item) => item.id)

      // Update the state with the new data
      setWinderData(newData)
      setWinderSequence(newSequence)

      // Update the sequence in Firestore
      try {
        const sequenceDocRef = doc(db, "sequence", "documentForWinderSequence")
        await updateDoc(sequenceDocRef, { winderSequence: newSequence })
        message.success("Sequence updated!")
      } catch (error) {
        console.error("Error updating sequence:", error)
        message.error("Failed to update sequence. Please try again.")
      }
    }
  }

  // Default columns for the table
  const defaultColumns = [
    {
      title: "Nr.",
      dataIndex: "orderNumber",
      align: "center",
      sorter: (a, b) => a.orderNumber - b.orderNumber,
      render: (value, record) => (
        <Space>
          {ASAPOrder(record)}
          {OrderAndPrefix(record)}
        </Space>
      ),
      width: "10%",
    },
    {
      title: "Priority",
      width: "5%",
      dataIndex: "id",
      align: "center",
      render: (id) => <Badge color="blue" count={winderSequence.indexOf(id) + 1} />,
    },
    {
      title: "Client",
      dataIndex: ["client", "client"],
      align: "center",
      width: "10%",
      ellipsis: {
        showTitle: false,
      },
    },
    {
      title: "Product",
      dataIndex: ["selectedProduct", "product", "productName"],
      align: "center",
      ellipsis: {
        showTitle: false,
      },
    },
    {
      title: "Manager",
      dataIndex: "user",
      align: "center",
      ellipsis: {
        showTitle: false,
      },
    },
    {
      title: "Manufacturer",
      dataIndex: ["manufacturer", "company"],
      align: "center",
      ellipsis: {
        showTitle: false,
      },
      width: "5%",
    },
    {
      title: "Machine",
      dataIndex: ["selectedProduct", "machine"],
      align: "center",
    },
    {
      title: "Width",
      dataIndex: ["selectedProduct", "labelWidth"],
      align: "center",
      width: "5%",
    },
    {
      title: "Height",
      dataIndex: ["selectedProduct", "labelHeight"],
      align: "center",
      width: "5%",
    },
    {
      title: "Req. Amount",
      dataIndex: "labelCount",
      align: "center",
    },
    {
      title: "Created",
      dataIndex: "created",
      align: "center",
      sorter: (a, b) => a.date.created.seconds - b.date.created.seconds,
      render: (value) => <Tag>{moment.unix(value.seconds).format("DD/MM/YYYY")}</Tag>,
    },
    {
      title: "Due D.",
      dataIndex: "orderDueDate",
      align: "center",
      sorter: (a, b) => a.orderDueDate.seconds - b.orderDueDate.seconds,
      key: "created",
      render: (value, record) => {
        const dueDate = moment.unix(value.seconds)
        const isOverdue = dueDate.isBefore(moment(), "day")
        if (isOverdue) {
          return <Tag color="red">{dueDate.format("DD.MM.YYYY")}</Tag>
        } else {
          return <Tag color="blue">{dueDate.format("DD.MM.YYYY")}</Tag>
        }
      },
    },
    {
      title: "Technical",
      dataIndex: "technicalPage",
      align: "center",
      render: (value) => <Image src={value} width={25} height={25} />,
    },
  ]

  return (
    <div style={{ padding: 15 }}>
      <Row gutter={[15, 15]}>
        <Col span={24}>
          <PageHeader text="Winder Sequence" icon={<ShoppingCartOutlined />} />
        </Col>
        <Col span={24}>
          <DndContext onDragEnd={onDragEnd}>
            <SortableContext
              items={winderData.map((i) => i.key)}
              strategy={verticalListSortingStrategy}>
              <Table
                size="small"
                dataSource={winderData}
                loading={loading}
                columns={defaultColumns}
                rowClassName={(record) => (record.priorityOrder ? "red-background" : "")}
                rowKey={(record) => record.id}
                pagination={{ pageSize: 100 }}
                components={{
                  body: {
                    row: SortableRow,
                  },
                }}
              />
            </SortableContext>
          </DndContext>
        </Col>
      </Row>
    </div>
  )
}
